import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import { PageHeader, Select, Spin, Empty, Alert } from "antd";
import { ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { ButtonItem, SelectItem, DatePickerItem, SwitchItem } from "../../../../Components/Items";
import EstadosLeyenda from "../../../../Components/EstadosLeyenda";
import LienzoMapaHotel from "./LienzoMapaHotel";
import HabitacionDetalleDrawer from "./HabitacionDetalleDrawer";
import { COLORES_ESTADO } from "./Components/HabitacionCanvas";

const { Option } = Select;

// Cada cuánto se vuelve a pedir la ocupación. El mapa lo ve recepción con la pantalla abierta todo el
// día, así que tiene que reflejar los check-ins que capturó el turno de al lado sin recargar. No usa
// WebSockets a propósito: producción son dos servidores detrás del balanceador y cualquier emisor
// nuevo tendría que replicarse por wsBus, que es bastante más trabajo del que justifica un mapa.
const MS_REFRESCO = 60000;

// La leyenda se arma desde los MISMOS colores que pinta la cajita (ver HabitacionCanvas.js), para que
// no puedan quedar desfasados.
const ESTADOS_LEYENDA = [
  { key: "libre", color: COLORES_ESTADO.libre.borde, label: COLORES_ESTADO.libre.etiqueta },
  { key: "ocupada", color: COLORES_ESTADO.ocupada.borde, label: COLORES_ESTADO.ocupada.etiqueta },
  { key: "saleHoy", color: COLORES_ESTADO.saleHoy.borde, label: COLORES_ESTADO.saleHoy.etiqueta },
  { key: "programada", color: COLORES_ESTADO.programada.borde, label: COLORES_ESTADO.programada.etiqueta },
  { key: "sinTarifa", color: COLORES_ESTADO.sinTarifa.borde, label: COLORES_ESTADO.sinTarifa.etiqueta },
];

const Indicador = ({ etiqueta, valor, color }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #eef0f6",
      borderRadius: 10,
      padding: "6px 14px",
      minWidth: 92,
      textAlign: "center",
    }}
  >
    <div style={{ fontSize: 20, fontWeight: 700, color: color || "#111827", lineHeight: 1.1 }}>{valor}</div>
    <div style={{ fontSize: 11, color: "#6b7280" }}>{etiqueta}</div>
  </div>
);

//Pantalla "Mapa de Hotel": se elige hotel, piso y fecha, y se ve el plano con cada habitación pintada
//según esté ocupada. La ocupación NO se guarda en ningún lado: sale de las ventas, del rango
//fechaLlegada/fechaSalida que lleva cada renglón de hospedaje (ver el backend en
//Controllers/Hoteleria/hoteleria.controllers.js/selectMapaHotel).
//
//Esta pantalla nunca edita el mapa: el botón "Configuración" lleva al editor (MapaHotelEditor.js), y
//tampoco vende: el doble clic en una habitación abre su detalle con un link a la venta. Mismo reparto
//de responsabilidades que MapaWms.js / MapaAlmacenEditor.js en el módulo de almacenes.
const MapaHotel = ({ empresaId, headersToken, history, location }) => {
  const [todosProyectos, setTodosProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(undefined);
  const [niveles, setNiveles] = useState([]);
  const [nivelHotelId, setNivelHotelId] = useState(undefined);
  const [fecha, setFecha] = useState(moment());
  // Prendido por default: para planear, lo que ya está comprometido por una venta programada importa
  // tanto como lo vendido. Se puede apagar para ver solo el compromiso firme.
  const [incluirProgramadas, setIncluirProgramadas] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [datosMapa, setDatosMapa] = useState(null);
  const [drawer, setDrawer] = useState({ visible: false, habitacionId: null });

  const instanciaFlowRef = useRef(null);

  const fechaTexto = fecha ? fecha.format("YYYY-MM-DD") : undefined;

  useEffect(() => {
    axios
      .post("proyectos/selectProyectos", { empresaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.status === 404) return;
        setTodosProyectos(res.data || []);
        const proyectoIdPrevio = location?.state?.proyectoId;
        if (proyectoIdPrevio) setProyectoId(proyectoIdPrevio);
      })
      .catch((error) => console.error("Error al cargar los hoteles:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar de hotel se recargan sus pisos y se selecciona el primero, para que la pantalla nunca
  // quede en un estado en el que hay hotel pero no hay lienzo que mostrar.
  useEffect(() => {
    if (!proyectoId) {
      setNiveles([]);
      setNivelHotelId(undefined);
      return;
    }
    axios
      .post("hoteleria/selectNivelesHotel", { empresaId, proyectoId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success !== 2) return;
        const nivelesCargados = res.data.niveles || [];
        setNiveles(nivelesCargados);
        const nivelPrevio = location?.state?.nivelHotelId;
        const nivelExiste = nivelesCargados.some((valor) => String(valor._id) === String(nivelPrevio));
        setNivelHotelId(nivelExiste ? nivelPrevio : nivelesCargados[0]?._id);
      })
      .catch((error) => console.error("Error al cargar los pisos:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, headersToken, proyectoId]);

  const cargarMapa = useCallback(
    (silencioso) => {
      if (!nivelHotelId) {
        setDatosMapa(null);
        return;
      }
      if (!silencioso) setCargando(true);
      axios
        .post(
          "hoteleria/selectMapaHotel",
          { empresaId, proyectoId, nivelHotelId, fecha: fechaTexto, incluirProgramadas },
          { headers: headersToken }
        )
        .then((res) => {
          if (res.data.success === 2) setDatosMapa(res.data);
        })
        .catch((error) => console.error("Error al cargar el mapa del hotel:", error))
        .finally(() => setCargando(false));
    },
    [empresaId, headersToken, proyectoId, nivelHotelId, fechaTexto, incluirProgramadas]
  );

  useEffect(() => {
    cargarMapa();
  }, [cargarMapa]);

  // Refresco silencioso: sin spinner y sin tocar `fitView`, para que no se mueva el encuadre mientras
  // alguien está mirando el plano.
  useEffect(() => {
    if (!nivelHotelId) return;
    const intervalo = setInterval(() => cargarMapa(true), MS_REFRESCO);
    return () => clearInterval(intervalo);
  }, [cargarMapa, nivelHotelId]);

  const abrirConfiguracion = () => {
    if (!proyectoId) return;
    history.push("/panel/mapaHotelEditor", { proyectoId, nivelHotelId });
  };

  const verVenta = (ventaId) => history.push("/panel/verVenta", { id: ventaId });
  const verVentaProgramada = (ventaProgramadaId) =>
    history.push("/panel/editarVentaProgramada", { id: ventaProgramadaId });

  const resumen = datosMapa?.resumen;
  const haySobreventa = (datosMapa?.ocupacion || []).some((valor) => valor.sobreventa);

  return (
    <>
      <PageHeader
        title="Mapa de Hotel"
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ButtonItem
              blanco
              size="small"
              icon={<ReloadOutlined />}
              disabled={!nivelHotelId}
              onClick={() => cargarMapa()}
            >
              Actualizar
            </ButtonItem>
            <ButtonItem
              size="small"
              className="botonAgregar"
              icon={<SettingOutlined />}
              disabled={!proyectoId}
              onClick={abrirConfiguracion}
            >
              Configuración
            </ButtonItem>
          </div>
        }
      />

      <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Hotel</div>
            <SelectItem width={240} placeholder="Hotel" value={proyectoId} onChange={setProyectoId}>
              {todosProyectos.map((valorProyecto) => (
                <Option key={valorProyecto._id} value={valorProyecto._id}>
                  {valorProyecto.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Piso</div>
            <SelectItem
              width={200}
              placeholder="Piso"
              value={nivelHotelId}
              disabled={!proyectoId}
              onChange={setNivelHotelId}
            >
              {niveles.map((valorNivel) => (
                <Option key={valorNivel._id} value={valorNivel._id}>
                  {valorNivel.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Fecha</div>
            <DatePickerItem
              width={150}
              value={fecha}
              allowClear={false}
              onChange={(valor) => setFecha(valor || moment())}
            />
          </div>

          {/* Lo que compromete una venta programada todavía no es una venta. Se muestra por default
              porque para planear la temporada es justo lo que hace falta, pero se puede apagar para
              ver solo lo firme. */}
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Ventas programadas</div>
            <SwitchItem
              checked={incluirProgramadas}
              onChange={setIncluirProgramadas}
              checkedChildren="Incluir"
              unCheckedChildren="Omitir"
            />
          </div>

          {resumen && (
            <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
              <Indicador etiqueta="Habitaciones" valor={resumen.total} />
              <Indicador etiqueta="Libres" valor={resumen.libres} color={COLORES_ESTADO.libre.texto} />
              <Indicador etiqueta="Ocupadas" valor={resumen.ocupadas} color={COLORES_ESTADO.ocupada.texto} />
              <Indicador etiqueta="Salen hoy" valor={resumen.salenHoy} color={COLORES_ESTADO.saleHoy.texto} />
              {incluirProgramadas && (
                <Indicador
                  etiqueta="Programadas"
                  valor={resumen.programadas}
                  color={COLORES_ESTADO.programada.texto}
                />
              )}
              <Indicador
                etiqueta="Ocupación"
                valor={`${
                  incluirProgramadas
                    ? resumen.porcentajeOcupacionConProgramadas
                    : resumen.porcentajeOcupacion
                }%`}
              />
            </div>
          )}
        </div>

        {resumen && resumen.sinTarifa > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${resumen.sinTarifa} habitación(es) sin insumo de tarifa asignado`}
            description="Se pintan en gris y no se pueden vender hasta que se les asigne su insumo en la configuración del mapa."
          />
        )}

        {haySobreventa && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Hay habitaciones con más de una estancia viva en esta fecha"
            description="Están marcadas con ⚠ en el plano. Da doble clic para ver qué ventas las están ocupando."
          />
        )}

        <div style={{ marginBottom: 8 }}>
          <EstadosLeyenda estados={ESTADOS_LEYENDA} compact />
        </div>

        <Spin spinning={cargando}>
          {!proyectoId ? (
            <Empty description="Elige un hotel para ver su mapa" style={{ padding: 60 }} />
          ) : niveles.length === 0 ? (
            <Empty
              description="Este hotel todavía no tiene pisos configurados"
              style={{ padding: 60 }}
            >
              <ButtonItem className="botonAgregar" onClick={abrirConfiguracion}>
                Configurar el mapa
              </ButtonItem>
            </Empty>
          ) : (datosMapa?.habitaciones || []).length === 0 ? (
            <Empty description="Este piso todavía no tiene habitaciones" style={{ padding: 60 }}>
              <ButtonItem className="botonAgregar" onClick={abrirConfiguracion}>
                Agregar habitaciones
              </ButtonItem>
            </Empty>
          ) : (
            <LienzoMapaHotel
              habitaciones={datosMapa.habitaciones}
              ocupacion={datosMapa.ocupacion}
              ocupacionProgramada={datosMapa.ocupacionProgramada}
              onInit={(instancia) => (instanciaFlowRef.current = instancia)}
              onHabitacionDoubleClick={(habitacionId) => setDrawer({ visible: true, habitacionId })}
            />
          )}
        </Spin>
      </div>

      <HabitacionDetalleDrawer
        visible={drawer.visible}
        habitacionId={drawer.habitacionId}
        fecha={fechaTexto}
        empresaId={empresaId}
        headersToken={headersToken}
        onVerVenta={verVenta}
        onVerVentaProgramada={verVentaProgramada}
        onClose={() => setDrawer({ visible: false, habitacionId: null })}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(MapaHotel);
